// src/pages/consulting/ConsultingHome.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ConsultingLayout from "./ConsultingLayout.js";
import {
  ArrowRight,
  BarChart3,
  Target,
  Users,
  Zap,
  Check,
  Layers,
  ChevronRight,
  Globe,
  Shield,
  TrendingUp,
  Database,
  Cloud,
  Smartphone,
  Cpu,
  MonitorSmartphone,
  Workflow,
} from "lucide-react";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html, body, #root {
    background: #0b0c0f;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  .ch-page {
    --bg:        #0b0c0f;
    --surface:   #141519;
    --surface-2: #1b1c21;
    --border:    rgba(255,255,255,0.07);
    --border-2:  rgba(255,255,255,0.13);
    --accent:    #c6a15b;
    --accent-2:  #8c6a32;
    --accent-soft: rgba(198,161,91,0.14);
    --green:     #6f9d80;
    --slate:     #8892a6;
    --white:     #f3efe7;
    --muted:     #55565c;
    --muted-2:   #8b8b92;
    --text:      #c9c6c0;

    background: #0b0c0f;
    color: var(--white);
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    padding-top: 0;
    position: relative;
  }

  .ch-bg-canvas {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .ch-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px);
    background-size: 64px 64px;
    -webkit-mask-image: radial-gradient(ellipse 70% 55% at 50% 0%, black 0%, transparent 75%);
    mask-image: radial-gradient(ellipse 70% 55% at 50% 0%, black 0%, transparent 75%);
  }

  .ch-bg-orb-1 {
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 900px;
    height: 500px;
    background: radial-gradient(ellipse, rgba(198,161,91,0.10) 0%, transparent 70%);
  }

  .ch-bg-orb-2 {
    position: absolute;
    bottom: 10%;
    left: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(111,157,128,0.06) 0%, transparent 70%);
  }

  .ch-bg-orb-3 {
    position: absolute;
    top: 40%;
    right: -8%;
    width: 420px;
    height: 420px;
    background: radial-gradient(circle, rgba(198,161,91,0.05) 0%, transparent 70%);
  }

  .ch-wrap {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;
    background: transparent;
  }

  .ch-main-shell {
    background: #0b0c0f;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  /* ── Hero ── */
  .ch-hero {
    padding: 48px 0 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  /* ── Badge / ticker éditorial ── */
  .ch-badge {
    display: inline-flex;
    align-items: center;
    width: min(760px, 100%);
    max-width: 100%;
    padding: 12px 0;
    border-radius: 0;
    background: transparent;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    margin-bottom: 36px;
    animation: fade-up 0.6s ease both;
    overflow: hidden;
    position: relative;
  }

  .ch-badge::before,
  .ch-badge::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 48px;
    z-index: 2;
    pointer-events: none;
  }

  .ch-badge::before {
    left: 0;
    background: linear-gradient(90deg, #0b0c0f, transparent);
  }

  .ch-badge::after {
    right: 0;
    background: linear-gradient(270deg, #0b0c0f, transparent);
  }

  .ch-badge-track {
    display: flex;
    align-items: center;
    gap: 18px;
    white-space: nowrap;
    animation: badge-scroll 42s linear infinite;
    will-change: transform;
  }

  .ch-badge-track:hover {
    animation-play-state: paused;
  }

  @keyframes badge-scroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .ch-badge-card {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 4px 0;
    border-radius: 0;
    background: transparent;
    border: none;
    color: var(--text);
    flex-shrink: 0;
  }

  .ch-badge-card:hover {
    color: var(--white);
  }

  .ch-badge-card:hover .ch-badge-icon-wrap {
    border-color: rgba(198,161,91,0.5);
    color: var(--accent);
  }

  .ch-badge-icon-wrap {
    width: 22px;
    height: 22px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--border-2);
    color: var(--muted-2);
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .ch-badge-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: inherit;
  }

  .ch-badge-sep {
    color: var(--accent);
    font-size: 10px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  .ch-hero-title {
    font-family: 'Fraunces', serif;
    font-optical-sizing: auto;
    font-size: clamp(2.9rem, 5.6vw, 5.2rem);
    font-weight: 500;
    line-height: 1.04;
    letter-spacing: -0.02em;
    margin-bottom: 28px;
    max-width: 840px;
    animation: fade-up 0.6s 0.1s ease both;
  }

  .ch-hero-title-plain { color: var(--white); display: block; }

  .ch-hero-title-grad {
    display: block;
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(120deg, #e8c77e 0%, #c6a15b 45%, #8c6a32 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .ch-hero-sub {
    font-size: 17px;
    line-height: 1.7;
    color: var(--muted-2);
    max-width: 500px;
    margin-bottom: 44px;
    animation: fade-up 0.6s 0.2s ease both;
  }

  .ch-hero-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 80px;
    animation: fade-up 0.6s 0.3s ease both;
  }

  .ch-btn-glow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 26px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.25s cubic-bezier(0.16,1,0.3,1);
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    color: #14100a;
    border: 1px solid rgba(198,161,91,0.4);
    box-shadow: 0 2px 10px rgba(0,0,0,0.35);
    position: relative;
    overflow: hidden;
  }

  .ch-btn-glow::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.12), transparent);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .ch-btn-glow:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.45);
  }

  .ch-btn-glow:hover::before { opacity: 1; }

  .ch-btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 26px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.25s ease;
    color: var(--text);
    border: 1px solid var(--border-2);
    background: rgba(255,255,255,0.02);
  }

  .ch-btn-ghost:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(198,161,91,0.35);
    color: var(--white);
    transform: translateY(-1px);
  }

  /* ── Stats ── */
  .ch-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--border);
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
    width: 100%;
    animation: fade-up 0.6s 0.4s ease both;
    margin-bottom: 80px;
  }

  .ch-stat {
    background: var(--surface);
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: background 0.2s;
    position: relative;
    overflow: hidden;
  }

  .ch-stat::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 2px;
    background: var(--accent);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  .ch-stat:hover { background: var(--surface-2); }
  .ch-stat:hover::after { transform: scaleX(1); }

  .ch-stat-num {
    font-family: 'Fraunces', serif;
    font-size: 2.4rem;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--white);
  }

  .ch-stat-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.04em;
    color: var(--muted-2);
  }

  .ch-stat-icon {
    position: absolute;
    top: 28px; right: 28px;
    color: var(--muted);
    opacity: 0.4;
  }

  /* ── Section header ── */
  .ch-section-head { margin-bottom: 48px; }

  .ch-section-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10.5px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--accent);
    margin-bottom: 16px;
  }

  .ch-section-tag-line {
    width: 20px; height: 1px;
    background: var(--accent);
    opacity: 0.5;
  }

  .ch-section-title {
    font-family: 'Fraunces', serif;
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--white);
    line-height: 1.2;
  }

  .ch-section-desc {
    margin-top: 14px;
    font-size: 15px;
    color: var(--muted-2);
    max-width: 480px;
    line-height: 1.65;
  }

  /* ── Bento Grid ── */
  .ch-bento {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 14px;
    margin-bottom: 100px;
  }

  .ch-bento-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 32px;
    transition: all 0.3s cubic-bezier(0.16,1,0.3,1);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .ch-bento-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(198,161,91,0.55), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .ch-bento-card:hover {
    border-color: rgba(198,161,91,0.25);
    background: var(--surface-2);
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.35);
  }

  .ch-bento-card:hover::before { opacity: 1; }

  .ch-bento-lg { grid-column: span 7; }
  .ch-bento-sm { grid-column: span 5; }
  .ch-bento-3  { grid-column: span 4; }

  .ch-bento-icon {
    width: 38px; height: 38px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    flex-shrink: 0;
  }

  .ch-bento-icon-purple { background: var(--accent-soft); border: 1px solid rgba(198,161,91,0.28); color: var(--accent); }
  .ch-bento-icon-green  { background: rgba(111,157,128,0.10); border: 1px solid rgba(111,157,128,0.24); color: var(--green); }
  .ch-bento-icon-blue   { background: rgba(136,146,166,0.10); border: 1px solid rgba(136,146,166,0.24); color: var(--slate); }

  .ch-bento-card h3 {
    font-family: 'Inter', sans-serif;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--white);
    margin-bottom: 10px;
  }

  .ch-bento-card p {
    font-size: 13.5px;
    color: var(--muted-2);
    line-height: 1.65;
    flex: 1;
  }

  .ch-bento-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--accent);
    text-decoration: none;
    margin-top: 24px;
    opacity: 0;
    transform: translateY(4px);
    transition: all 0.2s;
  }

  .ch-bento-card:hover .ch-bento-link { opacity: 1; transform: translateY(0); }

  .ch-feat-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
  }

  .ch-feat-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: var(--text);
    padding: 10px 14px;
    background: rgba(255,255,255,0.015);
    border: 1px solid var(--border);
    border-radius: 6px;
    transition: all 0.2s;
  }

  .ch-feat-item:hover {
    background: var(--accent-soft);
    border-color: rgba(198,161,91,0.22);
    color: var(--white);
  }

  .ch-feat-check {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(111,157,128,0.12);
    border: 1px solid rgba(111,157,128,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--green);
  }

  .ch-bento-deco {
    position: absolute;
    bottom: -40px; right: -40px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(198,161,91,0.05) 0%, transparent 70%);
    pointer-events: none;
  }

  /* ── Approach ── */
  .ch-approach-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;
    margin-bottom: 100px;
  }

  .ch-step-list { display: flex; flex-direction: column; }

  .ch-step {
    display: flex;
    gap: 20px;
    padding: 24px 0;
    border-bottom: 1px solid var(--border);
    cursor: default;
    transition: all 0.2s;
  }

  .ch-step:first-child { padding-top: 0; }
  .ch-step:last-child  { border-bottom: none; }

  .ch-step:hover .ch-step-num {
    color: var(--accent);
    border-color: rgba(198,161,91,0.35);
    background: var(--accent-soft);
  }

  .ch-step-num {
    flex-shrink: 0;
    width: 36px; height: 36px;
    border-radius: 6px;
    border: 1px solid var(--border-2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted-2);
    transition: all 0.2s;
    margin-top: 2px;
  }

  .ch-step-body h4 { font-size: 15px; font-weight: 600; color: var(--white); margin-bottom: 6px; }
  .ch-step-body p  { font-size: 13px; color: var(--muted-2); line-height: 1.6; }

  /* ── Visual panel ── */
  .ch-visual-panel {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 32px;
    position: relative;
    overflow: hidden;
  }

  .ch-visual-panel::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(198,161,91,0.45), transparent);
  }

  .ch-metrics { display: flex; flex-direction: column; gap: 16px; }
  .ch-metric-row { display: flex; flex-direction: column; gap: 8px; }
  .ch-metric-header { display: flex; justify-content: space-between; align-items: center; }
  .ch-metric-name { font-size: 12.5px; font-weight: 500; color: var(--text); }
  .ch-metric-val  { font-family: 'IBM Plex Mono', monospace; font-size: 12.5px; font-weight: 500; color: var(--white); }

  .ch-metric-bar {
    height: 4px;
    background: rgba(255,255,255,0.05);
    border-radius: 4px;
    overflow: hidden;
  }

  .ch-metric-fill {
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, var(--accent-2), var(--accent));
    animation: bar-in 1.2s cubic-bezier(0.16,1,0.3,1) both;
  }

  @keyframes bar-in { from { width: 0 !important; } }

  .ch-metric-fill-green { background: linear-gradient(90deg, #4c7a5f, var(--green)); }
  .ch-metric-fill-blue  { background: linear-gradient(90deg, #626b7d, var(--slate)); }

  .ch-visual-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--muted-2);
    margin-bottom: 24px;
  }

  /* ── CTA ── */
  .ch-cta {
     margin-bottom: 0;
    border-radius: 16px;
    padding: 80px 60px;
    text-align: center;
    position: relative;
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--border);
  }

  .ch-cta::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(198,161,91,0.09) 0%, transparent 70%);
    pointer-events: none;
  }

  .ch-cta-title {
    font-family: 'Fraunces', serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--white);
    margin-bottom: 16px;
    position: relative;
  }

  .ch-cta-sub {
    font-size: 16px;
    color: var(--muted-2);
    margin-bottom: 40px;
    max-width: 460px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    position: relative;
  }

  .ch-cta-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    position: relative;
  }

  /* ── Scroll Navigator ── */
  .ch-scroll-nav {
    position: fixed;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .ch-scroll-btn {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(20, 21, 25, 0.9);
    border: 1px solid rgba(255,255,255,0.08);
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    color: rgba(255,255,255,0.5);
    outline: none;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  }

  .ch-scroll-btn:hover {
    background: var(--accent-soft);
    border-color: rgba(198,161,91,0.4);
    color: var(--accent);
    transform: scale(1.08);
  }

  .ch-scroll-btn.disabled {
    opacity: 0.25;
    pointer-events: none;
  }

  .ch-scroll-btn svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .ch-scroll-progress-track {
    width: 2px;
    height: 80px;
    background: rgba(255,255,255,0.06);
    border-radius: 2px;
    overflow: hidden;
    position: relative;
  }

  .ch-scroll-progress-fill {
    width: 100%;
    background: linear-gradient(180deg, var(--accent), var(--accent-2));
    border-radius: 2px;
    transition: height 0.1s linear;
    position: absolute;
    top: 0;
    left: 0;
  }

  .ch-scroll-tooltip {
    position: absolute;
    right: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%);
    background: rgba(20,21,25,0.95);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.7);
    font-size: 11px;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 6px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    backdrop-filter: blur(8px);
  }

  .ch-scroll-btn:hover .ch-scroll-tooltip { opacity: 1; }

  /* ── Animations ── */
  @keyframes fade-up {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Responsive tablette ── */
  @media (max-width: 1024px) {
    .ch-wrap {
      padding: 0 24px;
    }

    .ch-hero {
      padding: 42px 0 70px;
    }

    .ch-hero-title {
      font-size: clamp(2.5rem, 6.6vw, 4.2rem);
      max-width: 720px;
    }

    .ch-hero-sub {
      max-width: 620px;
    }

    .ch-badge {
      width: min(100%, 680px);
    }

    .ch-badge-track {
      animation: badge-scroll 38s linear infinite;
    }

    .ch-bento-lg {
      grid-column: span 12;
    }

    .ch-bento-sm,
    .ch-bento-3 {
      grid-column: span 6;
    }

    .ch-approach-grid {
      grid-template-columns: 1fr;
      gap: 42px;
    }

    .ch-cta {
      padding: 64px 40px;
    }
  }

  /* ── Responsive mobile ── */
  @media (max-width: 768px) {
    .ch-wrap {
      padding: 0 18px;
    }

    .ch-hero {
      padding: 34px 0 56px;
    }

    .ch-hero-title {
      font-size: clamp(2.1rem, 10vw, 3rem);
      line-height: 1.06;
      margin-bottom: 20px;
      max-width: 100%;
    }

    .ch-hero-sub {
      font-size: 15px;
      line-height: 1.65;
      max-width: 100%;
      margin-bottom: 30px;
    }

    .ch-hero-actions {
      flex-direction: column;
      width: 100%;
      gap: 10px;
      margin-bottom: 48px;
    }

    .ch-btn-glow,
    .ch-btn-ghost {
      width: 100%;
      justify-content: center;
    }

    .ch-stats {
      grid-template-columns: 1fr;
      margin-bottom: 56px;
    }

    .ch-stat {
      padding: 24px 20px;
    }

    .ch-stat-num {
      font-size: 2rem;
    }

    .ch-bento {
      gap: 14px;
      margin-bottom: 70px;
    }

    .ch-bento-lg,
    .ch-bento-sm,
    .ch-bento-3 {
      grid-column: span 12;
    }

    .ch-bento-card {
      padding: 24px 20px;
    }

    .ch-approach-grid {
      grid-template-columns: 1fr;
      gap: 34px;
      margin-bottom: 0px;
    }

    .ch-section-head {
      margin-bottom: 30px;
    }

    .ch-section-title {
      font-size: clamp(1.6rem, 6vw, 2rem);
    }

    .ch-section-desc {
      max-width: 100%;
      font-size: 14px;
    }

    .ch-step {
      gap: 14px;
      padding: 18px 0;
    }

    .ch-step-num {
      width: 32px;
      height: 32px;
      font-size: 11px;
    }

    .ch-step-body h4 {
      font-size: 14px;
    }

    .ch-step-body p {
      font-size: 13px;
    }

    .ch-visual-panel {
      padding: 24px 20px;
      border-radius: 12px;
    }

    .ch-cta {
      padding: 46px 22px;
      margin-bottom: 70px;
    }

    .ch-cta-sub {
      font-size: 15px;
      margin-bottom: 28px;
    }

    .ch-cta-actions {
      flex-direction: column;
      width: 100%;
    }

    .ch-scroll-nav {
      display: none;
    }

    .ch-badge {
      width: 100%;
      max-width: 100%;
      margin-bottom: 24px;
      padding: 10px 0;
    }

    .ch-badge-track {
      gap: 14px;
      animation: badge-scroll 30s linear infinite;
    }

    .ch-badge-card {
      gap: 7px;
      min-width: max-content;
    }

    .ch-badge-icon-wrap {
      width: 20px;
      height: 20px;
    }

    .ch-badge-label {
      font-size: 9px;
      letter-spacing: 0.07em;
      white-space: nowrap;
    }

    .ch-badge-sep {
      font-size: 9px;
    }

    .ch-badge::before,
    .ch-badge::after {
      width: 24px;
    }
  }

  /* ── Très petits écrans ── */
  @media (max-width: 480px) {
    .ch-wrap {
      padding: 0 14px;
    }

    .ch-hero-title {
      font-size: 1.9rem;
    }

    .ch-hero-sub {
      font-size: 14px;
    }

    .ch-btn-glow,
    .ch-btn-ghost {
      padding: 12px 18px;
      font-size: 13px;
    }

    .ch-stat-icon {
      top: 20px;
      right: 20px;
    }

    .ch-badge-track {
      gap: 10px;
      animation: badge-scroll 26s linear infinite;
    }

    .ch-badge-card {
      gap: 6px;
    }

    .ch-badge-icon-wrap {
      width: 18px;
      height: 18px;
    }

    .ch-badge-label {
      font-size: 8px;
      letter-spacing: 0.05em;
    }

    .ch-cta-title {
      font-size: 1.7rem;
    }
  }
`;

const METRICS = [
  { name: "Satisfaction client", val: "95%", pct: 95, color: "" },
  { name: "Délai de livraison", val: "−35%", pct: 65, color: "ch-metric-fill-green" },
  { name: "ROI moyen constaté", val: "+2.4×", pct: 80, color: "ch-metric-fill-blue" },
];

const STEPS = [
  { n:"01", title:"Audit & Diagnostic", desc:"Analyse approfondie de votre SI, processus métier et objectifs stratégiques." },
  { n:"02", title:"Conception de la roadmap", desc:"Définition d'un plan d'action priorisé, aligné sur vos contraintes et ambitions." },
  { n:"03", title:"Déploiement agile", desc:"Mise en œuvre itérative avec points de contrôle réguliers et ajustements." },
  { n:"04", title:"Suivi & Optimisation", desc:"Mesure des KPIs, retours d'expérience et amélioration continue post-lancement." },
];

const SERVICES = [
  {
    icon: <Target size={18}/>,
    iconClass: "ch-bento-icon-purple",
    col: "ch-bento-lg",
    title: "Stratégie & Transformation",
    desc: "Nous co-construisons votre feuille de route digitale en alignant vos enjeux métiers avec les meilleures pratiques du secteur.",
    features: ["Audit organisationnel", "Architecture cible", "Change management"],
    link: "/consulting/services",
  },
  {
    icon: <Zap size={18}/>,
    iconClass: "ch-bento-icon-green",
    col: "ch-bento-sm",
    title: "Optimisation Opérationnelle",
    desc: "Réduisez les frictions, automatisez les tâches répétitives et libérez la capacité de vos équipes.",
    features: [],
    link: "/consulting/services",
  },
  {
    icon: <Globe size={18}/>,
    iconClass: "ch-bento-icon-blue",
    col: "ch-bento-3",
    title: "Expansion Internationale",
    desc: "Stratégies d'entrée sur de nouveaux marchés, conformité réglementaire et localisation.",
    features: [],
    link: "/consulting/services",
  },
  {
    icon: <Shield size={18}/>,
    iconClass: "ch-bento-icon-purple",
    col: "ch-bento-3",
    title: "Gouvernance & Risques",
    desc: "Cadres de gouvernance robustes, gestion des risques et conformité réglementaire.",
    features: [],
    link: "/consulting/services",
  },
  {
    icon: <TrendingUp size={18}/>,
    iconClass: "ch-bento-icon-green",
    col: "ch-bento-3",
    title: "Performance & Croissance",
    desc: "Identification des leviers de croissance, optimisation des revenus et pilotage par la donnée.",
    features: [],
    link: "/consulting/services",
  },
];

const BADGE_SERVICES = [
  { icon: <Target size={14} />, label: "Conseil & Transformation Digitale" },
  { icon: <Database size={14} />, label: "ERP & Solutions Métier" },
  { icon: <Workflow size={14} />, label: "Intégration des SI" },
  { icon: <BarChart3 size={14} />, label: "Pilotage de Projets" },
  { icon: <TrendingUp size={14} />, label: "Performance & Optimisation" },
  { icon: <Cloud size={14} />, label: "Cloud & Infrastructure" },
  { icon: <Shield size={14} />, label: "Cybersécurité & Gouvernance" },
  { icon: <Cpu size={14} />, label: "IA & Automatisation" },
  { icon: <MonitorSmartphone size={14} />, label: "Web & Plateformes Digitales" },
  { icon: <Smartphone size={14} />, label: "Applications Mobiles" },
  { icon: <Users size={14} />, label: "Formation & Accompagnement des équipes" },

  // Nouveaux services
  { icon: <Workflow size={14} />, label: "Infrastructure & Réseaux" },
  { icon: <Cloud size={14} />, label: "Infogérance complète" },
  {
    icon: <MonitorSmartphone size={14} />,
    label: "Location & achat de matériel informatique",
  },
  { icon: <Smartphone size={14} />, label: "Télécom & Connectivité" },
  { icon: <Users size={14} />, label: "Support & Assistance" },
  { icon: <Target size={14} />, label: "Conseil & Innovation" },
];

/* ── Scroll Navigator ── */
function ScrollNavigator() {
  const [progress, setProgress] = useState(0);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(pct);
      setAtTop(scrollTop <= 10);
      setAtBottom(pct >= 98);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollUp = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollDown = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });

  return (
    <div className="ch-scroll-nav">
      <button className={`ch-scroll-btn ${atTop ? "disabled" : ""}`} onClick={scrollUp} aria-label="Haut">
        <span className="ch-scroll-tooltip">Haut de page</span>
        <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
      </button>

      <div className="ch-scroll-progress-track">
        <div className="ch-scroll-progress-fill" style={{ height: `${progress}%` }}/>
      </div>

      <button className={`ch-scroll-btn ${atBottom ? "disabled" : ""}`} onClick={scrollDown} aria-label="Bas">
        <span className="ch-scroll-tooltip">Bas de page</span>
        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
  );
}

/* ── Main Component ── */
export default function ConsultingHome() {
  return (
    <ConsultingLayout>
      <style>{STYLES}</style>

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div className="ch-page" style={{ flex: 1 }}>
          <div className="ch-bg-canvas">
            <div className="ch-bg-grid"/>
            <div className="ch-bg-orb-1"/>
            <div className="ch-bg-orb-2"/>
            <div className="ch-bg-orb-3"/>
          </div>

          <ScrollNavigator/>

          <div className="ch-wrap ch-main-shell">
            <section className="ch-hero">

              <div className="ch-badge">
                <div className="ch-badge-track">
                  {[...BADGE_SERVICES, ...BADGE_SERVICES].map((item, i) => (
                    <React.Fragment key={i}>
                      <div className="ch-badge-card">
                        <span className="ch-badge-icon-wrap">{item.icon}</span>
                        <span className="ch-badge-label">{item.label}</span>
                      </div>
                      <span className="ch-badge-sep">•</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <h1 className="ch-hero-title">
                <span className="ch-hero-title-plain">Transformation</span>
                <span className="ch-hero-title-grad">pilotée par l'expertise.</span>
              </h1>

              <p className="ch-hero-sub">
                Nous alignons vos enjeux métiers avec la réalité technologique.
                De la stratégie au déploiement, nous restons à vos côtés.
              </p>

              <div className="ch-hero-actions">
                <Link to="/consulting/contact" className="ch-btn-glow">
                  Démarrer un projet <ArrowRight size={15}/>
                </Link>
                <Link to="/consulting/services" className="ch-btn-ghost">
                  Nos services <ChevronRight size={15}/>
                </Link>
              </div>

              <div className="ch-stats">
                {[
                  { num: "15+", label: "Projets livrés", icon: <Layers size={18}/> },
                  { num: "95%", label: "Clients satisfaits", icon: <Users size={18}/> },
                  { num: "12×", label: "ROI moyen constaté", icon: <BarChart3 size={18}/> },
                ].map((s, i) => (
                  <div className="ch-stat" key={i}>
                    <div className="ch-stat-icon">{s.icon}</div>
                    <div className="ch-stat-num">{s.num}</div>
                    <div className="ch-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="ch-section-head">
                <div className="ch-section-tag">
                  <span className="ch-section-tag-line"/>
                  Expertises
                </div>
                <h2 className="ch-section-title">Un accompagnement<br/>à chaque étape.</h2>
                <p className="ch-section-desc">
                  De l'audit initial à la mise en production, nos consultants seniors
                  s'impliquent concrètement dans vos projets.
                </p>
              </div>

              <div className="ch-bento">
                {SERVICES.map((s, i) => (
                  <div className={`ch-bento-card ${s.col}`} key={i}>
                    <div className={`ch-bento-icon ${s.iconClass}`}>{s.icon}</div>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>

                    {s.features.length > 0 && (
                      <div className="ch-feat-list">
                        {s.features.map((f, j) => (
                          <div className="ch-feat-item" key={j}>
                            <span className="ch-feat-check"><Check size={10}/></span>
                            {f}
                          </div>
                        ))}
                      </div>
                    )}

                    <Link to={s.link} className="ch-bento-link">
                      En savoir plus <ArrowRight size={13}/>
                    </Link>
                    <div className="ch-bento-deco"/>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="ch-approach-grid">
                <div>
                  <div className="ch-section-head">
                    <div className="ch-section-tag">
                      <span className="ch-section-tag-line"/>
                      Méthodologie
                    </div>
                    <h2 className="ch-section-title">Notre approche,<br/>structurée & agile.</h2>
                    <p className="ch-section-desc">
                      Une méthode éprouvée qui combine rigueur analytique
                      et adaptabilité pour garantir des résultats tangibles.
                    </p>
                  </div>

                  <div className="ch-step-list">
                    {STEPS.map((s, i) => (
                      <div className="ch-step" key={i}>
                        <div className="ch-step-num">{s.n}</div>
                        <div className="ch-step-body">
                          <h4>{s.title}</h4>
                          <p>{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="ch-visual-panel">
                  <div className="ch-visual-label">Performance client — moyenne</div>
                  <div className="ch-metrics">
                    {METRICS.map((m, i) => (
                      <div className="ch-metric-row" key={i}>
                        <div className="ch-metric-header">
                          <span className="ch-metric-name">{m.name}</span>
                          <span className="ch-metric-val">{m.val}</span>
                        </div>
                        <div className="ch-metric-bar">
                          <div
                            className={`ch-metric-fill ${m.color}`}
                            style={{ width: `${m.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="ch-cta">
              <h2 className="ch-cta-title">Prêt à transformer votre organisation ?</h2>
              <p className="ch-cta-sub">
                Échangeons sur vos enjeux lors d'un premier appel stratégique, sans engagement.
              </p>
              <div className="ch-cta-actions">
                <Link to="/consulting/contact" className="ch-btn-glow">
                  Prendre rendez-vous <ArrowRight size={15}/>
                </Link>
                <Link to="/consulting/a-propos" className="ch-btn-ghost">
                  Notre équipe <ChevronRight size={15}/>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </ConsultingLayout>
  );
}